/* eslint-disable max-lines */
import { type ColumnDef, createColumnHelper } from '@tanstack/table-core';
import { DatabaseIcon, UserIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { TableHeader } from '@/components/DataTable/TableHeader';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Toaster } from '@/hooks/use-toast';
import type { ProjectIntegrations } from '@/lib/queries/admin';

const columnsHelper = createColumnHelper<ProjectIntegrations>();

const OK = '✅ Onboarded';
const WARNING = '⚠️ Onboarded with issues';
const ERROR = '🚨 Onboarding failed';
const NOT_ONBOARDED = '❌ Not onboarded';
const PENDING = '⏳ Onboarding in progress';

const simpleValidator = (v: { status: string }) => v.status === 'active';
const simpleErrorValidator = (v: { status: string }) => v.status === 'error';
const bucketValidator = (v: { accessStatus: string; syncStatus: string }) =>
  v.accessStatus === 'active' && v.syncStatus === 'synced';
const bucketErrorValidator = (v: { accessStatus: string }) =>
  v.accessStatus === 'error';
const githubValidator = () => true;
const githubErrorValidator = () => false;

const getCell = <T,>(
  array: T[],
  value: string,
  validator: (v: T) => boolean,
  errorValidator: (v: T) => boolean,
) => {
  const okCount = array.filter(validator).length;
  const errorCount = array.filter(v => errorValidator(v)).length;
  const totalCount = array.length;

  return (
    <div>
      <p>{value}</p>
      <p className="text-gray-500 dark:text-gray-400">{okCount} OK</p>
      <p className="text-gray-500 dark:text-gray-400">
        {totalCount - okCount - errorCount} Pending
      </p>
      <p className="text-gray-500 dark:text-gray-400">{errorCount} Error</p>
    </div>
  );
};

const getEmoji = <T,>(
  array: T[],
  validator: (v: T) => boolean,
  errorValidator: (v: T) => boolean,
) => {
  const okCount = array.filter(validator).length;
  const errorCount = array.filter(v => errorValidator(v)).length;
  const totalCount = array.length;

  if (okCount > 0 && errorCount === 0) {
    return OK;
  } else if (okCount > 0) {
    return WARNING;
  } else if (errorCount > 0) {
    return ERROR;
  } else if (totalCount === 0) {
    return NOT_ONBOARDED;
  } else {
    return PENDING;
  }
};

export const getIntegrationsColumns = (
  impersonate: (id: string) => Promise<void>,
  defaultProjectId: string | undefined,
  toast: Toaster,
): ColumnDef<ProjectIntegrations>[] =>
  [
    columnsHelper.accessor('projectName', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="admin.table"
          type="input"
        />
      ),
      cell: ({ getValue, row }) => (
        <div className="flex flex-col">
          <div className="text-sm flex items-center">
            <p>{getValue()}</p>
            <TooltipProvider>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <div className="flex items-center border px-1 h-6 ml-2 rounded-sm gap-1">
                    <UserIcon className="h-4 w-4" />
                    <p>{row.original.members.length}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <div>
                    {row.original.members.map(m => (
                      <p key={m.id}>{m.email}</p>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="p-0 h-6 w-6 ml-1 border rounded-sm"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        row.original.neo4jDatabaseName,
                      );
                      toast({
                        title: 'global.copiedToClipboard',
                        duration: 800,
                      });
                    }}
                  >
                    <DatabaseIcon className="h-4 w-4 cursor-pointer" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {row.original.neo4jDatabaseName}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {row.original.members.find(m => m.role === 'admin')?.email}
          </div>
        </div>
      ),
    }),
    columnsHelper.accessor(
      c =>
        getEmoji(
          [...c.awsRoles, ...c.awsCredentials],
          simpleValidator,
          simpleErrorValidator,
        ),
      {
        id: 'awsCredentials',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            [...row.original.awsRoles, ...row.original.awsCredentials],
            getValue(),
            simpleValidator,
            simpleErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c =>
        getEmoji(c.githubInstallations, githubValidator, githubErrorValidator),
      {
        id: 'githubConfigurations',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.githubInstallations,
            getValue(),
            githubValidator,
            githubErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c => getEmoji(c.hcpCloudTokens, simpleValidator, simpleErrorValidator),
      {
        id: 'hcpCloudTokens',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.hcpCloudTokens,
            getValue(),
            simpleValidator,
            simpleErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c =>
        getEmoji(
          c.datadogApiCredentials,
          simpleValidator,
          simpleErrorValidator,
        ),
      {
        id: 'datadogApiCredentials',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.datadogApiCredentials,
            getValue(),
            simpleValidator,
            simpleErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor(
      c => getEmoji(c.s3_buckets, bucketValidator, bucketErrorValidator),
      {
        id: 's3Buckets',
        header: ({ column }) => (
          <TableHeader
            column={column}
            headersTranslationPath="admin.table"
            type="select"
          />
        ),
        cell: ({ getValue, row }) => {
          return getCell(
            row.original.s3_buckets,
            getValue(),
            bucketValidator,
            bucketErrorValidator,
          );
        },
      },
    ),
    columnsHelper.accessor('id', {
      header: () => <FormattedMessage id="admin.table.id.header" />,
      cell: ({ getValue }) => {
        if (defaultProjectId === undefined) {
          return null;
        }

        if (getValue() === defaultProjectId) {
          return <p>Current Project</p>;
        }

        return (
          <Button onClick={() => impersonate(getValue())} variant="outline">
            Impersonate
          </Button>
        );
      },
    }),
  ] as ColumnDef<ProjectIntegrations>[];
